import { formatDate } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth.service';
import { maxDaysPastValidator } from '../validators/created-validator';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { EventForm } from '../_models/event-form.model';
import { EditEventFormStore } from './_services/edit-event-form-store.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { StreappEvent } from 'src/app/models/streappevent.model';
import { MatSnackBar } from '@angular/material/snack-bar';

const MAX_DAYS_IN_PAST = 2;

@Component({
  selector: 'edit-event-form',
  standalone: false,
  templateUrl: './edit-event-form.component.html',
  styleUrl: './edit-event-form.component.scss'
})
export class EditEventFormComponent implements OnInit, OnDestroy{

  private originalCreatedValue = '';

  public eventFormStore = inject(EditEventFormStore);

  public authService = inject(AuthService);
  
  private fb = inject(FormBuilder);

  private activatedRoute = inject(ActivatedRoute);

  private router = inject(Router);

  private materialSnackbar = inject(MatSnackBar);

  public streappFormGroup: FormGroup<EventForm>;

  private readonly destroy$: Subject<boolean> = new Subject();
  
  public constructor() {
    const now = new Date();
    var formattedDate = formatDate(now, 'yyyy-MM-dd', 'en');
    formattedDate += 'T' + formatDate(new Date(), 'HH:mm', 'en');

    this.originalCreatedValue = formattedDate;

    this.eventFormStore.streappEvent().created = now;

    this.streappFormGroup = this.fb.group({
      detailsForm: this.fb.group({
        titel: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(150)]),
        beschrijving: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(5000)]),
        created: this.fb.nonNullable.control(formattedDate, [
          Validators.required,
          this.maxDaysPastOnlyIfChangedValidator(() => this.originalCreatedValue),
        ]),
      }),
      additionalDetailsForm: this.fb.group({
        privacyEnabled: this.fb.nonNullable.control(undefined, Validators.required),
        emergencyServicesPresent: this.fb.nonNullable.control(undefined, Validators.required),
        highImpactEvent: this.fb.nonNullable.control(undefined, Validators.required),
        helpFunctionForm: this.fb.group({
          victimsOrPersonsVisible: this.fb.nonNullable.control(false),
          minorsVisible: this.fb.nonNullable.control(false),
          emergencyRespondersOrBystandersWantExclusion: this.fb.nonNullable.control(false),
          suspectVisible: this.fb.nonNullable.control(false),
          licensePlatesVisible: this.fb.nonNullable.control(false),
        })
      })
    });
  }

  async ngOnInit(): Promise<void> {
    this.eventFormStore.isLoading.set(true);

    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe(async (params) => {
      let id = params['id'];
      
      var event = await this.eventFormStore.getAndSetEvent(id);
      if(!event) {
        this.router.navigate(['/']);
      }

      this.eventFormStore.initEventData(event);
      this.setFormGroup(event);

      this.eventFormStore.isLoading.set(false);
    });
  }
  
  ngOnDestroy(): void {
    this.eventFormStore.reset();
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private setFormGroup(event: StreappEvent) {
    var formattedDate = formatDate(event.created, 'yyyy-MM-dd', 'en');
    formattedDate += 'T' + formatDate(event.created, 'HH:mm', 'en');

    this.originalCreatedValue = formattedDate;
    
    this.streappFormGroup.controls.detailsForm.controls.titel.setValue(event.title);
    this.streappFormGroup.controls.detailsForm.controls.beschrijving.setValue(event.description);
    this.streappFormGroup.controls.detailsForm.controls.created.setValue(formattedDate);

    this.streappFormGroup.controls.additionalDetailsForm.controls.emergencyServicesPresent.setValue(event.emergencyServicesPresent);
    this.streappFormGroup.controls.additionalDetailsForm.controls.highImpactEvent.setValue(event.isHighImpactEvent);
  }

    private maxDaysPastOnlyIfChangedValidator(getOriginalValue: () => string): ValidatorFn {
    const baseValidator = maxDaysPastValidator(MAX_DAYS_IN_PAST);

    return (control: AbstractControl) => {
      const value = control.value as string | null;
      const original = getOriginalValue();

      if (!value || value === original) {
        return null; // unchanged => skip maxDaysPast check
      }

      return baseValidator(control);
    };
  }

  public onStepChange(event: StepperSelectionEvent) {
    this.eventFormStore.setStep(event.selectedIndex, true);

    if (this.eventFormStore.currentStep() === 1) {
      // trigger window resize event to make sure the map is rendered correctly when navigating back to the location step      
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    }
  }

  public nextStep() {
    this.eventFormStore.setStep((this.eventFormStore.currentStep() ?? 0) + 1);
  }

  public previousStep() {
    this.eventFormStore.setStep((this.eventFormStore.currentStep() ?? 0) - 1);
  }

  public async updateEvent(): Promise<void> {
    if(this.eventFormStore.isPublishing() || this.eventFormStore.isLoading()) return;
    
    try {
      this.eventFormStore.isPublishing.set(true);

      await this.eventFormStore.updateEventDetails();

      if(this.eventFormStore.uploadItems().length > 0) {
        await this.eventFormStore.updateEventMediaItems();
      }
      
      this.materialSnackbar.open(
        `Streapp succesvol geüpdate.`,
        'Ok',
        {
        duration: 5000,
        }
      );
      this.router.navigate(['detail', this.eventFormStore.streappEvent().id]);
    } catch (error) {
      console.log(error);

      this.materialSnackbar.open(
          `Fout bij updaten Streapp: ${error}. Probeer het nogmaals.`,
          'Ok',
          {
          duration: 5000,
          }
      );
    } finally {
      this.eventFormStore.isPublishing.set(false);
    }
  }

  cancel(): void {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe(async (params) => {
      let id = params['id'];
      this.router.navigate(['detail', id]);
    });
  }
}
